/* ================================================================
   HCS MOBILE CART DRAWER — Controller
   File: assets/cart-drawer-mobile.js
   Purpose: Custom element that replaces the vanilla Horizon
            <dialog>-based drawer on mobile. Listens for
            CartAddEvent, refetches /cart.js, re-renders the
            drawer body, and animates a bottom sheet in.
   Built:   2026-04-10
   Notes:
   - No <dialog> element (FB in-app browser bugs).
   - Uses position:fixed + transform.
   - Swipe-down to dismiss (40px threshold).
   - Body scroll lock while open.
   - Auto-refetches cart state on every open.
   ================================================================ */

(function () {
  if (customElements.get('drawer-m-component')) return;

  const CART_ADD_EVENT = 'cart:add'; // Horizon theme event name
  const CART_UPDATE_EVENT = 'cart:update';

  class DrawerMobileComponent extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._touchStartY = 0;
      this._touchCurrentY = 0;
      this._isDragging = false;
      this._bodyScrollY = 0;
    }

    connectedCallback() {
      this._root = this.querySelector('.drawer-m__root');
      this._sheet = this.querySelector('.drawer-m__sheet');
      this._backdrop = this.querySelector('.drawer-m__backdrop');
      this._body = this.querySelector('.drawer-m__body');
      this._handle = this.querySelector('.drawer-m__handle');

      // Bind handlers
      this._onCartAdd = this._onCartAdd.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onDelegatedClick = this._onDelegatedClick.bind(this);

      // Cart-add event (Horizon fires 'cart:add' via CartAddEvent class)
      document.addEventListener(CART_ADD_EVENT, this._onCartAdd);
      document.addEventListener(CART_UPDATE_EVENT, this._onCartUpdate);

      // Close interactions
      if (this._backdrop) this._backdrop.addEventListener('click', this._onBackdropClick);
      this.addEventListener('click', this._onCloseClick);
      this.addEventListener('click', this._onDelegatedClick);

      // Swipe-down dismiss
      if (this._sheet) {
        this._sheet.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this._sheet.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this._sheet.addEventListener('touchend', this._onTouchEnd, { passive: true });
      }

      // Esc key
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_ADD_EVENT, this._onCartAdd);
      document.removeEventListener(CART_UPDATE_EVENT, this._onCartUpdate);
      document.removeEventListener('keydown', this._onKeydown);
    }

    // ── Open / close API ──
    async open() {
      if (this._open) return;
      // Only operate on viewport <=767px (safety check; CSS also hides us above)
      if (window.innerWidth >= 768) return;

      // Refetch cart to get latest state
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
        await this._refreshDrawer(cart);
      } catch (e) {
        // Network error — fall back to current markup
        console.warn('[drawer-m] cart fetch failed:', e);
      }

      this._open = true;
      this._root?.setAttribute('data-open', 'true');
      // Lock body scroll (preserve position)
      this._bodyScrollY = window.scrollY;
      document.body.setAttribute('data-drawer-m-open', 'true');
      document.body.style.top = `-${this._bodyScrollY}px`;
    }

    close() {
      if (!this._open) return;
      this._open = false;
      this._root?.setAttribute('data-open', 'false');
      if (this._sheet) this._sheet.style.transform = ''; // reset any drag offset
      // Restore body scroll
      document.body.removeAttribute('data-drawer-m-open');
      document.body.style.top = '';
      window.scrollTo(0, this._bodyScrollY || 0);
    }

    // ── Event handlers ──
    _onCartAdd(e) {
      // v7.6.1: Stamp cart.attributes.reserved_at on first add (from any page —
      // PDP, homepage, collection, quick view). This ensures the cart reservation
      // timer on /cart has a real anchor time even if the user never visits /cart.
      // Fires for BOTH mobile and desktop adds because this class is rendered
      // globally from layout/theme.liquid via {% render 'cart-drawer-mobile' %}.
      // Idempotent: only POSTs update if reserved_at is missing. Silent fail —
      // next cart page visit will stamp as a fallback.
      this._stampReservedAtIfMissing();

      // 2026-04-18: Auto-open DISABLED — custom-header.liquid mc-drawer now handles
      // the mobile bottom sheet (converted from right-slide via @media <=959px CSS).
      // This file is kept alive ONLY for the reserved_at stamp above. Do not re-enable
      // open() — would double-open two drawers stacked on top of each other.
    }

    // v7.6.1: Cart reservation timer stamp — idempotent first-add hook
    _stampReservedAtIfMissing() {
      var LS_KEY = 'hcs_cart_reserved_at';
      /* Check localStorage first — faster than network call */
      try {
        var lsVal = localStorage.getItem(LS_KEY);
        if (lsVal && parseInt(lsVal, 10) > 0) {
          var elapsed = Date.now() - parseInt(lsVal, 10);
          if (elapsed < 24 * 60 * 60 * 1000) return; // still valid, skip
        }
      } catch(e){}

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          if (!cart || !cart.attributes || !cart.attributes.reserved_at) {
            var now = String(Date.now());
            try { localStorage.setItem(LS_KEY, now); } catch(e){}
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                attributes: { reserved_at: now }
              })
            });
          } else {
            /* Server has it — sync to localStorage */
            try { localStorage.setItem(LS_KEY, cart.attributes.reserved_at); } catch(e){}
          }
        })
        .catch(() => {
          /* Silent fail — localStorage fallback covers it */
        });
    }

    _onCartUpdate() {
      // If drawer is already open, refresh its content
      if (this._open) {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(r => r.json())
          .then(cart => this._refreshDrawer(cart))
          .catch(() => {});
      }
    }

    _onBackdropClick(e) {
      if (e.target === this._backdrop) this.close();
    }

    _onCloseClick(e) {
      const closeBtn = e.target.closest('[data-drawer-m-close]');
      if (closeBtn) {
        e.preventDefault();
        this.close();
      }
    }

    _onDelegatedClick(e) {
      // FBT Add-to-cart buttons inside the drawer
      const fbtAdd = e.target.closest('[data-drawer-m-fbt-add]');
      if (fbtAdd) {
        e.preventDefault();
        const variantId = fbtAdd.getAttribute('data-variant-id');
        if (!variantId) return;
        fbtAdd.textContent = 'Adding…';
        fbtAdd.style.pointerEvents = 'none';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then(r => r.json().then(d => ({ ok: r.ok, status: r.status, body: d })))
          .then(res => {
            if (!res.ok) {
              throw new Error((res.body && res.body.description) || ('HTTP ' + res.status));
            }
            return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
          })
          .then(cart => {
            this._refreshDrawer(cart);
            fbtAdd.textContent = '✓ Added';
            setTimeout(() => {
              fbtAdd.textContent = 'Add';
              fbtAdd.style.pointerEvents = '';
            }, 1500);
          })
          .catch(err => {
            console.warn('[drawer-m] FBT add failed:', err);
            fbtAdd.textContent = 'Try again';
            setTimeout(() => {
              fbtAdd.textContent = 'Add';
              fbtAdd.style.pointerEvents = '';
            }, 2000);
          });
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape' && this._open) this.close();
    }

    // ── Swipe-to-dismiss ──
    _onTouchStart(e) {
      if (this._body && this._body.scrollTop > 0) return; // don't start drag if content is scrolled
      this._touchStartY = e.touches[0].clientY;
      this._isDragging = true;
    }

    _onTouchMove(e) {
      if (!this._isDragging) return;
      this._touchCurrentY = e.touches[0].clientY;
      const dy = this._touchCurrentY - this._touchStartY;
      if (dy > 0) {
        e.preventDefault();
        if (this._sheet) this._sheet.style.transform = `translateY(${dy}px)`;
      }
    }

    _onTouchEnd() {
      if (!this._isDragging) return;
      this._isDragging = false;
      const dy = this._touchCurrentY - this._touchStartY;
      if (this._sheet) this._sheet.style.transform = '';
      if (dy > 60) this.close();
    }

    // ── Refresh drawer body from cart.js response ──
    async _refreshDrawer(cart) {
      if (!cart) return;

      const itemCount = cart.item_count || 0;
      const totalPrice = cart.total_price || 0;
      const items = cart.items || [];

      // Find the hero item (last added or last in list)
      const heroItem = items[items.length - 1];

      // Update count in header
      const countEl = this.querySelector('[data-drawer-m-count]');
      if (countEl) countEl.textContent = itemCount === 1 ? '(1 item)' : `(${itemCount} items)`;

      // Update header class (empty vs not)
      const headerEl = this.querySelector('.drawer-m__header');
      if (headerEl) {
        headerEl.classList.toggle('drawer-m__header--empty', itemCount === 0);
      }

      // Update hero row
      const heroWrap = this.querySelector('[data-drawer-m-hero]');
      const emptyWrap = this.querySelector('[data-drawer-m-empty]');
      const contentWrap = this.querySelector('[data-drawer-m-content]');

      if (itemCount === 0) {
        if (heroWrap) heroWrap.style.display = 'none';
        if (contentWrap) contentWrap.style.display = 'none';
        if (emptyWrap) emptyWrap.style.display = 'block';
      } else {
        if (emptyWrap) emptyWrap.style.display = 'none';
        if (contentWrap) contentWrap.style.display = 'block';
        if (heroWrap && heroItem) {
          heroWrap.style.display = '';
          this._renderHero(heroWrap, heroItem);
        }
      }

      // Update subtotal
      const subtotalEl = this.querySelector('[data-drawer-m-subtotal]');
      if (subtotalEl) subtotalEl.textContent = this._formatMoney(totalPrice);

      // Update checkout CTA href (always /checkout)
      const checkoutBtn = this.querySelector('[data-drawer-m-checkout]');
      if (checkoutBtn) checkoutBtn.href = '/checkout';
    }

    _renderHero(wrap, item) {
      const imgEl = wrap.querySelector('.drawer-m__hero-img');
      const titleEl = wrap.querySelector('.drawer-m__hero-title');
      const variantEl = wrap.querySelector('.drawer-m__hero-variant');
      const qtyEl = wrap.querySelector('.drawer-m__hero-qty');
      const priceEl = wrap.querySelector('.drawer-m__hero-price');

      if (imgEl) {
        imgEl.src = item.image ? item.image.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_160x160.$1$2') : '';
        imgEl.alt = item.product_title || '';
      }
      if (titleEl) titleEl.textContent = item.product_title || '';
      if (variantEl) {
        const v = (item.variant_options || []).filter(o => o && o !== 'Default Title').join(' · ');
        variantEl.textContent = v || '';
        variantEl.style.display = v ? '' : 'none';
      }
      if (qtyEl) qtyEl.textContent = `Qty ${item.quantity}`;
      if (priceEl) priceEl.textContent = this._formatMoney(item.final_line_price || item.line_price || 0);
    }

    _formatMoney(cents) {
      const v = (cents / 100).toFixed(2);
      return `$${v}`;
    }
  }

  customElements.define('drawer-m-component', DrawerMobileComponent);
})();
